import React from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CircleDollarSign,
  Clock3,
  ShieldAlert,
} from "lucide-react";
import { formatDate, formatMoney } from "../lib/api";

const urgencyStyles = {
  critical: {
    icon: ShieldAlert,
    border: "border-l-[#B91C1C]",
    iconWrap: "bg-[#FEE2E2] text-[#B91C1C]",
    reason: "text-[#991B1B]",
  },
  high: {
    icon: AlertTriangle,
    border: "border-l-[#D97706]",
    iconWrap: "bg-[#FEF3C7] text-[#92400E]",
    reason: "text-[#92400E]",
  },
  medium: {
    icon: Clock3,
    border: "border-l-[#1D4ED8]",
    iconWrap: "bg-[#DBEAFE] text-[#1D4ED8]",
    reason: "text-[#1D4ED8]",
  },
};

const AttentionRow = ({ item, onOpen }) => {
  const style = urgencyStyles[item.urgency] || urgencyStyles.medium;
  const Icon = item.reason_code === "overdue_money" ? CircleDollarSign : style.icon;
  const direction = item.direction === "to_me" ? "Promised to us" : "Promised by us";

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`attention-item-${item.id}`}
      className={`group w-full text-left border-b border-l-4 border-ledger-border ${style.border} bg-white px-5 py-5 md:px-6 transition-colors hover:bg-[#FAFAF8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ledger-ink focus-visible:ring-inset`}
    >
      <div className="flex items-start gap-4">
        <div className={`mt-0.5 h-9 w-9 shrink-0 rounded-sm grid place-items-center ${style.iconWrap}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-sans text-sm font-semibold text-ledger-ink">
              {item.client_organization}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ledger-muted">
              {direction}
            </span>
          </div>
          <div className={`mt-1 font-sans text-sm font-semibold ${style.reason}`}>
            {item.reason}
          </div>
          <div className="mt-1 text-sm text-ledger-sub">{item.promise_text}</div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-ledger-muted">
            {item.expected_completion && <span>Due {formatDate(item.expected_completion)}</span>}
            {!item.expected_completion && item.timeframe_text && <span>{item.timeframe_text}</span>}
            {item.outstanding_amount != null && (
              <span className="text-[#78350F]">
                {formatMoney(item.outstanding_amount)} outstanding
              </span>
            )}
            <span className="hidden md:inline truncate max-w-xl">
              Evidence: “{item.evidence_quote}”
            </span>
          </div>
        </div>
        <ArrowUpRight
          className="h-4 w-4 shrink-0 text-ledger-muted transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ledger-ink"
          aria-hidden="true"
        />
      </div>
    </button>
  );
};

export const NeedsAttention = ({ data, loading, onOpen }) => (
  <section data-testid="needs-attention" aria-labelledby="needs-attention-title">
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#B91C1C]">Priority queue</div>
        <h2 id="needs-attention-title" className="mt-1 font-display text-3xl md:text-4xl text-ledger-ink">
          Needs attention
        </h2>
        <p className="mt-1 text-sm text-ledger-sub">
          The few client commitments most likely to delay work, cash, or decisions.
        </p>
      </div>
      {!loading && data && (
        <div className="font-mono text-[11px] uppercase tracking-wider text-ledger-muted">
          Showing {data.count} of {data.total_requiring_attention}
        </div>
      )}
    </div>

    <div className="border border-ledger-border bg-ledger-surface">
      {loading ? (
        <div className="divide-y divide-ledger-border" data-testid="attention-loading">
          {[0, 1, 2].map((key) => (
            <div key={key} className="px-6 py-6 animate-pulse">
              <div className="h-3 w-32 bg-neutral-200 rounded" />
              <div className="mt-3 h-4 w-2/3 bg-neutral-200 rounded" />
              <div className="mt-2 h-3 w-1/2 bg-neutral-100 rounded" />
            </div>
          ))}
        </div>
      ) : data?.items?.length ? (
        data.items.map((item) => <AttentionRow key={item.id} item={item} onOpen={onOpen} />)
      ) : (
        <div className="px-6 py-12 text-center" data-testid="attention-empty">
          <div className="font-display text-2xl text-ledger-ink">Nothing needs intervention.</div>
          <div className="mt-1 text-sm text-ledger-sub">Open commitments are on track.</div>
        </div>
      )}
    </div>
  </section>
);

const MATERIAL_CHANGE_TYPES = new Set([
  "revised",
  "blocked",
  "cancelled",
  "partial_payment",
  "condition_satisfied",
  "condition_failed",
  "status_fulfilled",
  "fulfilled_with_evidence",
]);

export const ChangedRecently = ({ events = [], loading, onOpen }) => {
  const changes = events.filter((event) => MATERIAL_CHANGE_TYPES.has(event.type)).slice(0, 5);

  return (
    <section className="mt-12" data-testid="changed-recently" aria-labelledby="changed-recently-title">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-sm border border-ledger-border bg-white grid place-items-center">
          <Activity className="h-4 w-4 text-ledger-sub" aria-hidden="true" />
        </div>
        <div>
          <h2 id="changed-recently-title" className="font-display text-2xl text-ledger-ink">Changed recently</h2>
          <p className="text-sm text-ledger-sub">The latest movement in the commitments you are tracking.</p>
        </div>
      </div>

      <div className="border border-ledger-border bg-white">
        {loading ? (
          <div className="h-28 grid place-items-center font-mono text-xs text-ledger-muted">Loading changes…</div>
        ) : changes.length ? (
          <ul className="divide-y divide-ledger-border">
            {changes.map((event, index) => (
              <li key={`${event.commitment_id}-${event.at}-${index}`}>
                <button
                  type="button"
                  onClick={() => onOpen({ id: event.commitment_id })}
                  data-testid={`recent-change-${index}`}
                  className="w-full px-5 py-4 text-left flex items-start justify-between gap-4 hover:bg-[#FAFAF8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ledger-ink focus-visible:ring-inset"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ledger-ink">{event.head}</div>
                    <div className="mt-0.5 text-[12px] text-ledger-sub truncate">{event.detail}</div>
                  </div>
                  <div className="shrink-0 font-mono text-[11px] text-ledger-muted">
                    {formatDate(event.at)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-6 py-10 text-center text-sm text-ledger-muted">
            Revisions, payments, blockers, cancellations, and fulfilments will appear here.
          </div>
        )}
      </div>
    </section>
  );
};
